<script lang="ts">
	import { fade } from 'svelte/transition';
	import type { TransitionConfig } from 'svelte/transition';
	import {
		PROMO_COLLAPSE_MS,
		PROMO_FADE_IN_MS,
		PROMO_FADE_OUT_MS,
		prefersReducedMotion,
		promoCollapse,
		promoFade,
		promoFadeCollapse,
		type UiTransitionType,
	} from 'src/helpers/ui_transitions';

	/**
	 * Optional mount/destroy transitions around slotted content.
	 * Prefer this for `{#if}` containers. For layout-sensitive nodes
	 * (gallery slides, flex rail items), import helpers from
	 * `ui_transitions` and apply `out:` / `transition:` directly.
	 */
	export let transitionType: UiTransitionType = 'fade';
	/** Alias for transitionType (newer call sites). */
	export let type: UiTransitionType | undefined;
	export let inDuration = 400;
	export let outDuration = 200;
	/** When false, render children with no transition. */
	export let enabled = true;
	/**
	 * `promo` applies snappy dismiss timings (180/200/220).
	 * Default keeps legacy in=400 / out=200 for existing call sites.
	 */
	export let preset: 'default' | 'promo' = 'default';

	$: resolvedType = type ?? transitionType;

	$: resolvedIn =
		preset === 'promo'
			? resolvedType === 'fade'
				? PROMO_FADE_IN_MS
				: PROMO_COLLAPSE_MS
			: inDuration;

	$: resolvedOut =
		preset === 'promo'
			? resolvedType === 'fade'
				? PROMO_FADE_OUT_MS
				: PROMO_COLLAPSE_MS
			: outDuration;

	$: motionIn = prefersReducedMotion() ? 0 : resolvedIn;
	$: motionOut = prefersReducedMotion() ? 0 : resolvedOut;

	const runIn = (node: Element, params: { duration: number }): TransitionConfig => {
		const kind = type ?? transitionType;
		const duration = params.duration;
		if (kind === 'collapse') {
			return promoCollapse(node, { duration, opacity: false });
		}
		if (kind === 'fadeCollapse') {
			return promoFadeCollapse(node, { duration });
		}
		return preset === 'promo' ? promoFade(node, { duration }) : fade(node, { duration });
	};

	const runOut = (node: Element, params: { duration: number }): TransitionConfig => {
		const kind = type ?? transitionType;
		const duration = params.duration;
		if (kind === 'collapse') {
			return promoCollapse(node, { duration, opacity: false });
		}
		if (kind === 'fadeCollapse') {
			return promoFadeCollapse(node, { duration });
		}
		return preset === 'promo' ? promoFade(node, { duration }) : fade(node, { duration });
	};
</script>

{#if enabled}
	<div class="width-full" in:runIn={{ duration: motionIn }} out:runOut={{ duration: motionOut }}>
		<slot />
	</div>
{:else}
	<div class="width-full">
		<slot />
	</div>
{/if}
